import deepEqual from "fast-deep-equal";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, use, useMemo, useRef, useState } from "react";
import { createApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../api/adminApi";
import type {
  BusinessCentralObjectType,
  Field,
  FieldIdToLookupReferences,
  FieldIdToValueMap,
  FieldsCitationsMap,
  FieldsConfidenceMap,
  GetRelatedObjectsResponse,
  LookupObjectReference,
  ObjectTypeToLookupReferences,
  PageLayout,
  RelatedObjectsUpdateRequests,
} from "../../api/types/objectTypes";
import usePageLeaveConfirmation from "../../hooks/usePageLeaveConfirmation";
import type { EmailFieldEditMode, FieldValuesState } from "./FieldValuesContext";
import { isAnyRequiredFieldEmpty } from "./fieldValuesManagement";
import { collectRelatedCitations } from "./helpers";
import ObjectCardContext from "./ObjectCardContext";
import {
  areRelatedObjectRowsValid,
  getValidationErrorsByObjectType,
  getValidLookupOptionsOfRelatedObjects,
  type ValidationErrorsByRow,
} from "./relatedObjectsValidation";
import type { RelatedObjectValuesState } from "./relatedObjectValuesState";
import {
  getInitialRelatedObjectValuesState,
  getRelatedObjectFieldsByObjectType,
  getRelatedObjectsUpdateRequests,
  initRowsAction,
} from "./relatedObjectValuesState";

const getObjectsAsLookupReferences = withErrorHandling(adminApi.getObjectsAsLookupReferences);

const hasUnsavedChanges = (
  fieldValues: FieldIdToValueMap,
  storedFieldValues: FieldIdToValueMap,
  relatedObjectValuesState: RelatedObjectValuesState
) => !deepEqual(fieldValues, storedFieldValues) || relatedObjectValuesState.dirtyObjectTypes.size > 0;

export interface FieldValuesSectionProps {
  objectType: string;
  objectId: string;
  businessCentralType: BusinessCentralObjectType | undefined;
  fields: Field[];
  cardLayout: PageLayout;
  fieldValues: FieldIdToValueMap;
  fieldsConfidence?: FieldsConfidenceMap;
  fieldsCitations?: FieldsCitationsMap;
  lookupObjects?: FieldIdToLookupReferences;
  allowEditing: boolean;
  saveFieldValues: (fieldValues: FieldIdToValueMap, relatedObjects?: RelatedObjectsUpdateRequests) => Promise<boolean>;
  emailFieldEditMode?: EmailFieldEditMode;
  isLoading?: boolean;
}

export interface FieldValuesEditor {
  fieldValuesState: FieldValuesState;
  relatedObjectValuesState: RelatedObjectValuesState;
  relatedObjectsResp: GetRelatedObjectsResponse | undefined;
  relatedObjectsError: ApiError | undefined;
  isFetchingRelatedObjects: boolean;
  lookupOptionsByObjectType: ObjectTypeToLookupReferences | undefined;
  lookupOptionsOfRelatedObjectsByObjectType: Record<string, ObjectTypeToLookupReferences> | undefined;
  relatedObjectFieldsByObjectType: Record<string, Field[]>;
  validationErrorsByObjectType: Record<string, ValidationErrorsByRow>;
  areButtonsDisabled: boolean;
  isSaveDisabled: boolean;
  isBusy: boolean;
  setRelatedObjectValuesState: Dispatch<SetStateAction<RelatedObjectValuesState>>;
  handleRelatedObjectValuesChange: Dispatch<SetStateAction<RelatedObjectValuesState>>;
  handleFieldValuesStateChange: (stateUpdate: Partial<FieldValuesState>) => void;
  applyFieldValues: (fieldValues: FieldIdToValueMap) => void;
  handleLookupOptionsNeeded: (lookupObjectTypes: string[]) => Promise<void>;
  handleAddLookupOption: (objectType: string, lookupObjectReference: LookupObjectReference) => void;
  handleSaveClick: () => Promise<void>;
  handleCancelClick: () => void;
}

const useFieldValuesEditor = (
  {
    objectType,
    objectId,
    fields,
    cardLayout,
    fieldValues,
    allowEditing,
    saveFieldValues,
    isLoading,
  }: FieldValuesSectionProps,
  adjustRelatedObjectFields?: (fieldsByObjectType: Record<string, Field[]>) => Record<string, Field[]>
): FieldValuesEditor => {
  const objectCard = use(ObjectCardContext);
  const { sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);

  const [fieldValuesState, setFieldValuesState] = useState<FieldValuesState>({
    fieldValues,
    isValid: true,
    fieldEditState: "viewing",
  });

  const [relatedObjectValuesState, setRelatedObjectValuesState] = useState(getInitialRelatedObjectValuesState);

  const setHasUnsavedEdits = objectCard?.setHasUnsavedEdits;

  const handleRelatedObjectValuesChange = useCallback(
    (action: SetStateAction<RelatedObjectValuesState>) => {
      setRelatedObjectValuesState(action);
      setHasUnsavedEdits?.(true);
    },
    [setHasUnsavedEdits]
  );

  const [lazyLookupOptions, setLazyLookupOptions] = useState<ObjectTypeToLookupReferences>({});
  const requestedLookupTypesRef = useRef(new Set<string>());

  const { setPromptOnPageLeave } = usePageLeaveConfirmation(false);

  const getLookupOptions = useCallback(
    () =>
      allowEditing && fields.some((f) => f.type === "Lookup") && !isLoading
        ? adminApi.getLookupOptions(objectType, objectId)
        : Promise.resolve(createApiResponse<ObjectTypeToLookupReferences>({})),
    [allowEditing, fields, objectType, objectId, isLoading]
  );

  const [lookupOptionsByObjectType, , { isFetching: isFetchingLookupOptions, setData: setLookupOptions }] =
    useFetch(getLookupOptions);

  const handleLookupOptionsNeeded = useCallback(
    async (lookupObjectTypes: string[]) => {
      const typesToLoad = [...new Set(lookupObjectTypes)].filter((type) => !requestedLookupTypesRef.current.has(type));
      typesToLoad.forEach((type) => requestedLookupTypesRef.current.add(type));

      await Promise.all(
        typesToLoad.map(async (type) => {
          const [references, error] = await getObjectsAsLookupReferences(type);
          if (error) {
            logError(error, "[useFieldValuesEditor] getObjectsAsLookupReferences");
            sendNotificationError("Failed to load options");
            requestedLookupTypesRef.current.delete(type);
            return;
          }

          setLazyLookupOptions((prev) => ({ ...prev, [type]: references }));
        })
      );
    },
    [sendNotificationError]
  );

  const getRelatedObjects = useCallback(() => {
    if (!objectId || !cardLayout.relatedObjectGrids?.length) {
      return Promise.resolve(createApiResponse<GetRelatedObjectsResponse>({ relatedObjectsByType: [] }));
    }

    return adminApi.getRelatedObjects(objectType, objectId, {
      relatedObjects: cardLayout.relatedObjectGrids.map((layout) => ({
        objectType: layout.objectType,
        fieldIds: layout.fieldIds,
      })),
      includeLookupOptions: allowEditing,
    });
  }, [allowEditing, cardLayout.relatedObjectGrids, objectId, objectType]);

  const [
    relatedObjectsResp,
    relatedObjectsError,
    { isFetching: isFetchingRelatedObjects, fetch: fetchRelatedObjects },
  ] = useFetch(getRelatedObjects, {
    onSetData: (data) => {
      setRelatedObjectValuesState(initRowsAction(data.relatedObjectsByType));
      setHasUnsavedEdits?.(
        hasUnsavedChanges(fieldValuesState.fieldValues, fieldValues, getInitialRelatedObjectValuesState())
      );
      objectCard?.setRelatedCitations(collectRelatedCitations(data.relatedObjectsByType));
    },
  });

  const lookupOptionsOfRelatedObjectsByObjectType = useMemo(
    () =>
      lookupOptionsByObjectType && relatedObjectsResp && allowEditing
        ? getValidLookupOptionsOfRelatedObjects(
            fieldValuesState.fieldValues,
            fields,
            lookupOptionsByObjectType,
            relatedObjectsResp.relatedObjectsByType.map((x) =>
              x.lookupOptions ? { ...x, lookupOptions: { ...x.lookupOptions, ...lazyLookupOptions } } : x
            )
          )
        : undefined,
    [
      allowEditing,
      fieldValuesState.fieldValues,
      fields,
      lookupOptionsByObjectType,
      relatedObjectsResp,
      lazyLookupOptions,
    ]
  );

  const relatedObjectFieldsByObjectType = useMemo(() => {
    if (!cardLayout.relatedObjectGrids || !relatedObjectsResp) {
      return {};
    }

    const fieldsByObjectType = getRelatedObjectFieldsByObjectType(
      cardLayout.relatedObjectGrids,
      relatedObjectsResp.relatedObjectsByType
    );
    return adjustRelatedObjectFields ? adjustRelatedObjectFields(fieldsByObjectType) : fieldsByObjectType;
  }, [cardLayout.relatedObjectGrids, relatedObjectsResp, adjustRelatedObjectFields]);

  const validationErrorsByObjectType = useMemo(
    () =>
      allowEditing
        ? getValidationErrorsByObjectType(
            relatedObjectValuesState.rowsByObjectType,
            relatedObjectFieldsByObjectType,
            lookupOptionsOfRelatedObjectsByObjectType
          )
        : {},
    [
      allowEditing,
      lookupOptionsOfRelatedObjectsByObjectType,
      relatedObjectFieldsByObjectType,
      relatedObjectValuesState.rowsByObjectType,
    ]
  );

  const handleAddLookupOption = useCallback(
    (objectType: string, lookupObjectReference: LookupObjectReference) => {
      setLookupOptions((prev) => {
        if (!prev) {
          return { [objectType]: [lookupObjectReference] };
        }

        const updatedOptions: ObjectTypeToLookupReferences = { ...prev };
        if (!updatedOptions[objectType]) {
          updatedOptions[objectType] = [];
        }

        if (updatedOptions[objectType].some((option) => option.objectId === lookupObjectReference.objectId)) {
          return prev;
        }

        updatedOptions[objectType].push(lookupObjectReference);

        return updatedOptions;
      });
    },
    [setLookupOptions]
  );

  const applyFieldValues = (newFieldValues: FieldIdToValueMap) => {
    setPromptOnPageLeave(true);
    setFieldValuesState((prev) => ({ ...prev, fieldValues: { ...prev.fieldValues, ...newFieldValues } }));
    setHasUnsavedEdits?.(
      hasUnsavedChanges({ ...fieldValuesState.fieldValues, ...newFieldValues }, fieldValues, relatedObjectValuesState)
    );
  };

  const handleFieldValuesStateChange = (stateUpdate: Partial<FieldValuesState>) => {
    setPromptOnPageLeave(true);
    setFieldValuesState((prev) => ({ ...prev, ...stateUpdate }));
    setHasUnsavedEdits?.(
      hasUnsavedChanges(stateUpdate.fieldValues ?? fieldValuesState.fieldValues, fieldValues, relatedObjectValuesState)
    );
  };

  const handleSaveClick = async () => {
    const changedFieldValues = Object.entries(fieldValuesState.fieldValues).reduce<FieldIdToValueMap>(
      (result, [fieldId, value]) => {
        if (
          !deepEqual(value, fieldValues[fieldId]) &&
          fields.some((f) => f.id === fieldId && !f.attributes.includes("ReadOnly"))
        ) {
          result[fieldId] = value;
        }
        return result;
      },
      {}
    );

    const relatedObjectsUpdateRequests = getRelatedObjectsUpdateRequests(
      relatedObjectValuesState,
      cardLayout.relatedObjectGrids ?? [],
      objectId
    );

    setIsSaving(true);
    const isSaved = await saveFieldValues(changedFieldValues, relatedObjectsUpdateRequests);
    setIsSaving(false);

    if (isSaved && relatedObjectsUpdateRequests) {
      fetchRelatedObjects();
    }

    if (isSaved) {
      setHasUnsavedEdits?.(false);
    }

    setPromptOnPageLeave(!isSaved);
  };

  const handleCancelClick = () => {
    if (fieldValuesState.fieldEditState === "editing") {
      handleFieldValuesStateChange({ isValid: true, fieldEditState: "canceling" });
    } else {
      handleFieldValuesStateChange({
        fieldValues: structuredClone(fieldValues),
        isValid: true,
        fieldEditState: "viewing",
      });
    }

    setRelatedObjectValuesState(initRowsAction(relatedObjectsResp?.relatedObjectsByType ?? []));

    setPromptOnPageLeave(false);
    setHasUnsavedEdits?.(false);
  };

  const hasChanges = hasUnsavedChanges(fieldValuesState.fieldValues, fieldValues, relatedObjectValuesState);

  const areThereEmptyRequiredFields = isAnyRequiredFieldEmpty(fields, fieldValuesState.fieldValues);

  const isStateValid =
    fieldValuesState.isValid && !areThereEmptyRequiredFields && areRelatedObjectRowsValid(validationErrorsByObjectType);

  const areButtonsDisabled = fieldValuesState.fieldEditState === "editing" || isSaving || !hasChanges;

  return {
    fieldValuesState,
    relatedObjectValuesState,
    relatedObjectsResp,
    relatedObjectsError,
    isFetchingRelatedObjects,
    lookupOptionsByObjectType,
    lookupOptionsOfRelatedObjectsByObjectType,
    relatedObjectFieldsByObjectType,
    validationErrorsByObjectType,
    areButtonsDisabled,
    isSaveDisabled: areButtonsDisabled || !isStateValid,
    isBusy: isLoading || isFetchingLookupOptions || isFetchingRelatedObjects || isSaving,
    setRelatedObjectValuesState,
    handleRelatedObjectValuesChange,
    handleFieldValuesStateChange,
    applyFieldValues,
    handleLookupOptionsNeeded,
    handleAddLookupOption,
    handleSaveClick,
    handleCancelClick,
  };
};

export default useFieldValuesEditor;
